import CallIcon from '@mui/icons-material/Call'
import { styled } from '@mui/material'
import Button from '@mui/material/Button'
import React from 'react'
import { useSelector } from 'react-redux'

import { Form } from '@/app/component/form'
import SubmitButton from '@/app/component/form/submit-button'
import Loading from '@/app/component/guard/loading'
import { useGetContact } from '@/app/module/contacts/hooks/use-get-contact'
import { useGetContactPermissions } from '@/app/module/contacts/hooks/use-get-contact-permissions'
import { useUpdateContact } from '@/app/module/contacts/hooks/use-update-contact'
import { UpdateContactValues } from '@/app/module/contacts/types'
import { useGetCustomFields } from '@/app/module/custom-fields/hooks/use-get-custom-fields'
import { toggleCallDialog } from '@/app/module/user/store/actions'
import { selectDefaultCountryCode, selectLanguages } from '@/app/module/utils/store/selectors'
import { useAppDispatch } from '@/app/store/redux-hooks'
import { SelectOption } from '@/app/types'

import { ActionsContainer, FormContainer } from './common'
import DeleteProtectionToggle from './delete-protection-toggle'
import { getUpdateContactDefaultValues, getUpdateContactFields, getUpdateContactSchema } from './specs'
import { filterRestrictedFieldValues } from './utils'

type Props = {
  itemId: number

  onExit: () => void
}

const UpdateContactForm: React.FC<Props> = ({ itemId, onExit }) => {
  const dispatch = useAppDispatch()
  const countryCode = useSelector(selectDefaultCountryCode)
  const languageOptions = useSelector(selectLanguages) as SelectOption<string>[]
  const {
    data: cfData,
    isFetching: isCFFetching,
    isLoading: isCFLoading,
    isUninitialized: isCFUninitialized,
  } = useGetCustomFields()
  const {
    data: contact,
    isFetching: isContactFetching,
    isLoading: isContactLoading,
    isUninitialized: isContactUninitialized,
  } = useGetContact(itemId)
  const [updateContact, { isLoading: isUpdating }] = useUpdateContact()

  const [noAutodelete, setNoAutodelete] = React.useState(contact?.noAutodelete || false)
  const [changed, setChanged] = React.useState(false)
  React.useEffect(() => {
    setNoAutodelete(contact?.noAutodelete || false)
  }, [contact?.noAutodelete])
  const handleNoAutodeleteChange = React.useCallback((value: boolean) => {
    setNoAutodelete(value)
    setChanged(true)
  }, [])

  const customFields = React.useMemo(() => cfData?.allCustomfields || [], [cfData])

  const permissions = useGetContactPermissions()

  const fields = React.useMemo(
    () => getUpdateContactFields(permissions, customFields, languageOptions, countryCode),
    [countryCode, customFields, languageOptions, permissions],
  )
  const schema = React.useMemo(() => getUpdateContactSchema(permissions, customFields), [customFields, permissions])

  const defaultValues = React.useMemo(
    () => getUpdateContactDefaultValues(contact, customFields, permissions),
    [contact, customFields, permissions],
  )

  const handleSubmit = React.useCallback(
    async (data: UpdateContactValues) => {
      const updated = await updateContact(
        itemId,
        filterRestrictedFieldValues(permissions, customFields, data),
        noAutodelete,
      )
      if (!updated) {
        return
      }

      onExit()
    },
    [customFields, itemId, noAutodelete, permissions, updateContact, onExit],
  )

  const showLoading =
    isUpdating ||
    isCFFetching ||
    isCFLoading ||
    isCFUninitialized ||
    isContactFetching ||
    isContactLoading ||
    isContactUninitialized
  const showForm = !!contact

  return (
    <Loading isLoading={showLoading} testId="contact-item">
      <CallContainer>
        <Button
          id="item-contact-call-button"
          style={{ zIndex: 999 }}
          size="small"
          variant="outlined"
          color="primary"
          onClick={() =>
            dispatch(
              toggleCallDialog({
                contactId: itemId,
                contactName: contact?.firstName || `contact (${itemId})`,
                isOpen: true,
              }),
            )
          }
          startIcon={<CallIcon />}
        >
          <span
            style={{
              maxWidth: '150px',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            Call {contact?.firstName || contact?.fullPhoneNumber}
          </span>
        </Button>
      </CallContainer>
      {showForm && (
        <FormContainer id="contact-item-form">
          <Form
            defaultValues={defaultValues}
            fields={fields}
            schema={schema}
            shouldReset
            onSubmit={handleSubmit}
            renderActions={({ isDirty }) => (
              <ActionsContainer>
                <Button id="contact-back-button" variant="outlined" onClick={() => onExit()}>
                  Back
                </Button>
                <SubmitButton id="contact-save-button" isDirty={isDirty || changed} isSubmitting={isUpdating}>
                  Save
                </SubmitButton>
              </ActionsContainer>
            )}
          >
            <DeleteProtectionToggle checked={noAutodelete} isGroup={false} onChange={handleNoAutodeleteChange} />
          </Form>
        </FormContainer>
      )}
    </Loading>
  )
}

const CallContainer = styled('div')({
  display: 'flex',
  justifyContent: 'flex-end',
  width: '100%',
  marginTop: -30,
  paddingBottom: 10,
})

export default UpdateContactForm
